import { Tabs, BoxProps } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { Z_INDEX } from 'styles/zIndex';
import { TabHeader } from 'types/tab';
import { CustomTab } from './CustomTab';

export enum TabBarVariant {
  ARTICLE = 'ARTICLE',
  SEARCH = 'SEARCH',
  FAVORITE = 'FAVORITE',
  WORKSHEET = 'WORKSHEET',
}

type TabBarProps = {
  tabs: TabHeader[];
  position?: BoxProps['position'];
  top?: BoxProps['top'];
  children?: React.ReactNode;
  variant?: TabBarVariant;
  activeTabIndex?: number;
  onTabChange?: (index: number) => void;
  px?: BoxProps['px'];
};

export const TabBar = ({
  tabs,
  position,
  top,
  children,
  variant = TabBarVariant.ARTICLE,
  activeTabIndex,
  onTabChange,
  px,
}: TabBarProps): ReactElement => {
  const justifyContent =
    variant === TabBarVariant.SEARCH || variant === TabBarVariant.WORKSHEET
      ? 'flex-start'
      : 'space-between';
  const isFitted = variant !== TabBarVariant.SEARCH && variant !== TabBarVariant.WORKSHEET;

  const getBorderColor = (tabVariant: TabBarVariant): string => {
    switch (tabVariant) {
      case TabBarVariant.ARTICLE:
        return 'border.accent.active';
      case TabBarVariant.WORKSHEET:
        return 'background.accent';
      case TabBarVariant.SEARCH:
      case TabBarVariant.FAVORITE:
        return 'background.accent';
      default:
        return '';
    }
  };

  // Index 0 is valid; do not use truthiness on activeTabIndex or `value` becomes undefined for "Alle".
  const hasControlledIndex =
    typeof activeTabIndex === 'number' && activeTabIndex >= 0 && activeTabIndex < tabs.length;

  return (
    <Tabs.Root
      width="100%"
      fitted={isFitted}
      defaultValue={hasControlledIndex ? tabs[activeTabIndex].tabName : tabs[0].tabName}
      value={hasControlledIndex ? tabs[activeTabIndex].tabName : undefined}
      onValueChange={(details) =>
        // TODO: Rethink about this ? Use ID instead of name/value of the tab for switch, logical bug
        onTabChange?.(tabs.findIndex((tab) => tab.tabName === details.value))
      }
      variant="plain"
      css={{
        '--tabs-indicator-bg': 'transparent',
        '--tabs-indicator-shadow': 'none',
        '--tabs-trigger-radius': '0px',
      }}
    >
      <Tabs.List
        overflowX="auto"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        bg="background.primary"
        _after={
          variant === TabBarVariant.WORKSHEET
            ? {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '1px',
                bg: 'border.accent',
                pointerEvents: 'none',
              }
            : undefined
        }
        color="text.quaternary"
        justifyContent={justifyContent}
        alignItems="space-between"
        gap={variant === TabBarVariant.WORKSHEET ? 's' : undefined}
        px={px || { base: '12px', md: '40px' }}
        py={
          variant === TabBarVariant.ARTICLE || variant === TabBarVariant.WORKSHEET ? '0px' : '10px'
        }
        position={position || 'relative'}
        top={top}
        zIndex={Z_INDEX.BASE}
        display="flex"
      >
        {tabs.map((tab: TabHeader) => (
          <CustomTab {...tab} variant={variant} key={tab.tabName} />
        ))}
        <Tabs.Indicator
          borderBottom={variant === TabBarVariant.WORKSHEET ? '2px solid' : '3px solid'}
          borderColor={getBorderColor(variant)}
          bottom="0"
          height={variant === TabBarVariant.WORKSHEET ? '2px' : '100%'}
          zIndex={1}
        />
      </Tabs.List>
      {children}
    </Tabs.Root>
  );
};
